import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { ArrowUpRight, FileText, Landmark, ReceiptEuro, Send, Upload } from "lucide-react";
import api from "@/lib/api";
import { eur, fmtDate, invId, normalizeList, statusOf } from "@/lib/invoice";
import { StatusBadge } from "@/components/StatusBadge";

export default function Dashboard() {
  const [invoices, setInvoices] = useState([]);
  const [usage, setUsage] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [inv, usg, sub] = await Promise.allSettled([
          api.get("/invoices"),
          api.get("/usage"),
          api.get("/subscription"),
        ]);
        if (inv.status === "fulfilled") setInvoices(normalizeList(inv.value.data));
        if (usg.status === "fulfilled") setUsage(usg.value.data);
        if (sub.status === "fulfilled") setSubscription(sub.value.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const kpis = useMemo(() => {
    const ttc = invoices.reduce((s, i) => s + (Number(i.total_ttc) || 0), 0);
    const octroi = invoices.reduce((s, i) => s + (Number(i.total_octroi) || 0), 0);
    const sent = invoices.filter((i) => ["sent", "validated", "accepted", "paid"].includes(statusOf(i))).length;
    return [
      { label: "Factures", value: String(invoices.length), icon: FileText, testid: "kpi-invoices" },
      { label: "Volume TTC", value: eur(ttc), icon: ReceiptEuro, testid: "kpi-ttc" },
      { label: "Octroi de mer", value: eur(octroi), icon: Landmark, testid: "kpi-octroi" },
      { label: "Envoyées PDP", value: String(sent), icon: Send, testid: "kpi-pdp" },
    ];
  }, [invoices]);

  const chartData = useMemo(() => {
    const months = {};
    const now = new Date();
    for (let k = 5; k >= 0; k--) {
      const d = new Date(now.getFullYear(), now.getMonth() - k, 1);
      months[`${d.getFullYear()}-${d.getMonth()}`] = {
        name: d.toLocaleDateString("fr-FR", { month: "short" }),
        total: 0,
      };
    }
    invoices.forEach((i) => {
      const d = new Date(i.issue_date || i.created_at);
      if (Number.isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (months[key]) months[key].total += Number(i.total_ttc) || 0;
    });
    return Object.values(months);
  }, [invoices]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="dashboard-loading">
        <div className="h-10 w-10 rounded-full border-2 border-[#2563EB] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="overline-label mb-2">Tableau de bord</p>
          <h1 className="font-display font-light tracking-tighter text-3xl md:text-4xl">
            Vue d'ensemble
            {subscription?.plan && (
              <span className="ml-3 align-middle font-mono text-[11px] uppercase tracking-[0.2em] text-[#00D4FF] border border-[#00D4FF]/30 rounded-full px-3 py-1">
                {subscription.plan}
              </span>
            )}
          </h1>
        </div>
        <Link
          to="/app/importer"
          data-testid="dashboard-import-cta"
          className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold hover:bg-[#00D4FF] hover:text-[#050505] hover:-translate-y-0.5 transition-[transform,background-color,color] duration-200"
        >
          <Upload className="h-4 w-4" />
          Importer une facture
        </Link>
      </div>

      {(() => {
        const subActive = subscription && !["none", "trial", "cancelled", "canceled", "expired"].includes((subscription.status || "none").toLowerCase());
        if (subActive || !subscription?.trial) return null;
        const limit = Number(subscription.trial.limit) || 3;
        const remaining = subscription.trial.remaining ?? Math.max(0, limit - (Number(subscription.trial.used) || 0));
        return (
          <Link
            to="/app/abonnement"
            data-testid="trial-banner-dashboard"
            className={`flex items-center justify-between gap-3 rounded-2xl border px-5 py-3.5 mb-8 text-sm transition-colors duration-200 ${remaining > 0 ? "border-[#00D4FF]/30 bg-[#00D4FF]/5 hover:border-[#00D4FF]/60" : "border-[#FF3B30]/40 bg-[#FF3B30]/5 hover:border-[#FF3B30]/60"}`}
          >
            <span className="text-zinc-300">
              {remaining > 0
                ? `Essai gratuit : il vous reste ${remaining} facture${remaining > 1 ? "s" : ""} offerte${remaining > 1 ? "s" : ""} sur ${limit}`
                : "Essai gratuit terminé — passez à une formule pour continuer"}
            </span>
            <span className="text-[#00D4FF] font-semibold shrink-0">Voir les formules</span>
          </Link>
        );
      })()}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="kpi-grid">
        {kpis.map(({ label, value, icon: Icon, testid }, i) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.07 }}
            data-testid={testid}
            className="rounded-2xl border border-white/10 bg-[#0A0E17] p-5 hover:border-[#00D4FF]/30 transition-colors duration-300"
          >
            <div className="flex items-center justify-between">
              <p className="overline-label">{label}</p>
              <Icon className="h-4 w-4 text-zinc-600" />
            </div>
            <p className="mt-3 font-display text-2xl md:text-3xl font-light tracking-tight truncate">{value}</p>
          </motion.div>
        ))}
      </div>

      {usage && (usage.quota || usage.limit) && (
        <div className="rounded-2xl border border-white/10 bg-[#0A0E17] p-5 mb-8" data-testid="usage-panel">
          <div className="flex justify-between items-center mb-3">
            <p className="overline-label">Quota de conversions</p>
            <p className="font-mono text-xs text-zinc-400">
              {usage.used ?? usage.conversions ?? 0} / {usage.quota ?? usage.limit}
            </p>
          </div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#2563EB] to-[#00D4FF]"
              style={{ width: `${Math.min(100, ((usage.used ?? usage.conversions ?? 0) / (usage.quota ?? usage.limit ?? 1)) * 100)}%` }}
            />
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 rounded-2xl border border-white/10 bg-[#0A0E17] p-6" data-testid="chart-panel">
          <p className="overline-label mb-6">Volume TTC — 6 derniers mois</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <XAxis dataKey="name" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} width={50} />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.04)" }}
                  contentStyle={{ background: "#0A0E17", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }}
                  formatter={(v) => [eur(v), "TTC"]}
                />
                <Bar dataKey="total" fill="#2563EB" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-[#0A0E17] p-6" data-testid="recent-panel">
          <div className="flex items-center justify-between mb-5">
            <p className="overline-label">Récentes</p>
            <Link to="/app/factures" data-testid="recent-see-all" className="text-xs text-[#00D4FF] hover:text-white inline-flex items-center gap-1 transition-colors duration-200">
              Tout voir <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          {invoices.length === 0 ? (
            <p className="text-sm text-zinc-500 py-8 text-center" data-testid="recent-empty">
              Aucune facture. Importez votre premier PDF.
            </p>
          ) : (
            <ul className="space-y-1">
              {invoices.slice(0, 6).map((inv) => (
                <li key={invId(inv)}>
                  <Link
                    to={`/app/factures/${invId(inv)}`}
                    data-testid={`recent-invoice-${invId(inv)}`}
                    className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 hover:bg-white/5 transition-colors duration-200"
                  >
                    <div className="min-w-0">
                      <p className="font-mono text-sm truncate">{inv.invoice_number || invId(inv)}</p>
                      <p className="text-xs text-zinc-500">{fmtDate(inv.issue_date || inv.created_at)}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-mono text-sm">{eur(inv.total_ttc)}</p>
                      <StatusBadge invoice={inv} />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
